import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, Table, Form, FormGroup, Label, Input, Badge, Card, CardBody } from 'reactstrap'
import { Modal } from '@byscott-io/corebyscott'
import { withUserStore } from '../../../contexts/UserStore'
import { deepCamelizeKeys } from '../../../modules/KeyTransform'
import Lex from '../../Shared/Lex'

const OAuthApplications = ({ userStore, className }) => {
  const [applications, setApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [createModal, setCreateModal] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [tokensModal, setTokensModal] = useState(false)
  const [currentApp, setCurrentApp] = useState(null)
  const [appTokens, setAppTokens] = useState([])
  const getDefaultRedirectUri = () => {
    const protocol = window.location.protocol
    const host = window.location.host
    return `${protocol}//${host}/oauth/callback`
  }

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    allowed_audiences: ['WebApp'],
    allowed_scopes: ['read'],
    redirect_uris: [getDefaultRedirectUri()]
  })

  const availableAudiences = ['WebApp', 'MobileApp', 'API', 'ThirdParty']
  const availableScopes = [
    'read', 'write', 'accounts:read', 'accounts:write', 
    'users:read', 'users:write', 'priorities:read', 'priorities:write',
    'strategies:read', 'strategies:write', 'goals:read', 'goals:write',
    'tasks:read', 'tasks:write'
  ]

  useEffect(() => {
    if (userStore.bearerToken) {
      loadApplications()
    }
  }, [userStore.bearerToken])

  const loadApplications = () => {
    setLoading(true)
    const { bearerToken } = userStore
    
    fetch('/api/v1/oauth_applications', {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'JWT_AUD': 'WebApp',
        'Authorization': bearerToken
      }
    })
      .then(response => response.json())
      .then(data => {
        console.log('OAuth API response:', data) // Debug log
        const camelizedApps = deepCamelizeKeys(data.oauth_applications || [])
        console.log('Camelized apps:', camelizedApps) // Debug log
        setApplications(camelizedApps)
        setLoading(false)
      })
      .catch(error => {
        console.error('Error loading OAuth applications:', error)
        setLoading(false)
      })
  }

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      allowed_audiences: ['WebApp'],
      allowed_scopes: ['read'],
      redirect_uris: [getDefaultRedirectUri()]
    })
    setCurrentApp(null)
    setCreateModal(true)
  }

  const handleEdit = (app) => {
    setFormData({
      name: app.name,
      description: app.description || '',
      allowed_audiences: app.allowedAudiences || ['WebApp'],
      allowed_scopes: app.allowedScopes || ['read'],
      redirect_uris: app.redirectUris || [getDefaultRedirectUri()]
    })
    setCurrentApp(app)
    setEditModal(true)
  }

  const handleDelete = (app) => {
    setCurrentApp(app)
    setDeleteModal(true)
  }

  const handleViewTokens = (app) => {
    setCurrentApp(app)
    loadAppTokens(app.id)
    setTokensModal(true)
  }

  const loadAppTokens = (appId) => {
    const { bearerToken } = userStore
    
    fetch(`/api/v1/oauth_applications/${appId}/tokens`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'JWT_AUD': 'WebApp',
        'Authorization': bearerToken
      }
    })
      .then(response => response.json())
      .then(data => {
        console.log('Tokens API response:', data)
        const camelizedTokens = deepCamelizeKeys(data.oauth_access_tokens || [])
        setAppTokens(camelizedTokens)
      })
      .catch(error => {
        console.error('Error loading OAuth tokens:', error)
        setAppTokens([])
      })
  }

  const handleRevokeToken = (tokenId) => {
    const { bearerToken } = userStore
    
    fetch(`/api/v1/oauth_applications/${currentApp.id}/tokens/${tokenId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'JWT_AUD': 'WebApp',
        'Authorization': bearerToken
      }
    })
      .then(() => {
        loadAppTokens(currentApp.id)
        loadApplications() // Refresh the main list to update token counts
      })
      .catch(error => {
        console.error('Error revoking token:', error)
      })
  }

  const handleSubmit = () => {
    const { bearerToken } = userStore
    const url = currentApp ? `/api/v1/oauth_applications/${currentApp.id}` : '/api/v1/oauth_applications'
    const method = currentApp ? 'PUT' : 'POST'
    
    fetch(url, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'JWT_AUD': 'WebApp',
        'Authorization': bearerToken
      },
      body: JSON.stringify({
        oauth_application: {
          ...formData,
          account_id: userStore.account.id
        }
      })
    })
      .then(response => response.json())
      .then(() => {
        setCreateModal(false)
        setEditModal(false)
        loadApplications()
      })
      .catch(error => {
        console.error('Error saving OAuth application:', error)
      })
  }

  const handleDeleteConfirm = () => {
    const { bearerToken } = userStore
    
    fetch(`/api/v1/oauth_applications/${currentApp.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'JWT_AUD': 'WebApp',
        'Authorization': bearerToken
      }
    })
      .then(() => {
        setDeleteModal(false)
        setCurrentApp(null)
        loadApplications()
      })
      .catch(error => {
        console.error('Error deleting OAuth application:', error)
      })
  }

  const toggleAudience = (audience) => {
    const newAudiences = formData.allowed_audiences.includes(audience)
      ? formData.allowed_audiences.filter(a => a !== audience)
      : [...formData.allowed_audiences, audience]
    setFormData({ ...formData, allowed_audiences: newAudiences })
  }

  const toggleScope = (scope) => {
    const newScopes = formData.allowed_scopes.includes(scope)
      ? formData.allowed_scopes.filter(s => s !== scope)
      : [...formData.allowed_scopes, scope]
    setFormData({ ...formData, allowed_scopes: newScopes })
  }

  if (loading) {
    return <div className={className}><div className="text-center p-4">Loading OAuth applications...</div></div>
  }

  return (
    <div className={className}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4><Lex term="oauth-applications" /></h4>
          <p className="text-muted">
            Manage third-party applications that can access your organization&apos;s data through OAuth.
          </p>
        </div>
        <Button color="primary" onClick={handleCreate}>
          <Lex term="create" /> <Lex term="application" />
        </Button>
      </div>

      {applications.length === 0 ? (
        <Card>
          <CardBody className="text-center p-4">
            <p>No OAuth applications configured.</p>
            <Button color="primary" onClick={handleCreate}>
              Create Your First Application
            </Button>
          </CardBody>
        </Card>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>Application Name</th>
              <th>Client ID</th>
              <th>Audiences</th>
              <th>Scopes</th>
              <th>Active Tokens</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.map((app) => (
              <tr key={app.id}>
                <td>
                  <div>
                    <strong>{app.name}</strong>
                    {app.description && <div className="text-muted small">{app.description}</div>}
                  </div>
                </td>
                <td>
                  <code className="small">{app.clientId}</code>
                </td>
                <td>
                  {(app.allowedAudiences || []).map(audience => (
                    <Badge key={audience} color="info" className="me-1">
                      {audience}
                    </Badge>
                  ))}
                </td>
                <td>
                  {(app.allowedScopes || []).slice(0, 3).map(scope => (
                    <Badge key={scope} color="secondary" className="me-1">
                      {scope}
                    </Badge>
                  ))}
                  {app.allowedScopes && app.allowedScopes.length > 3 && (
                    <Badge color="light">+{app.allowedScopes.length - 3} more</Badge>
                  )}
                </td>
                <td>
                  {app.activeTokensCount ? (
                    <Button 
                      size="sm" 
                      color="info" 
                      onClick={() => handleViewTokens(app)}
                    >
                      {app.activeTokensCount} Active
                    </Button>
                  ) : (
                    <span className="text-muted">None</span>
                  )}
                </td>
                <td>
                  <Button size="sm" color="outline-primary" className="me-2" onClick={() => handleEdit(app)}>
                    Edit
                  </Button>
                  <Button size="sm" color="outline-danger" onClick={() => handleDelete(app)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="cbs-scope">
        {/* Create/Edit Modal */}
        <Modal
          open={createModal || editModal}
          onClose={() => { setCreateModal(false); setEditModal(false) }}
          title={`${currentApp ? 'Edit' : 'Create'} OAuth Application`}
          size="lg"
          footer={
            <>
              <Button color="secondary" onClick={() => { setCreateModal(false); setEditModal(false) }}>Cancel</Button>
              <Button color="primary" onClick={handleSubmit}>{currentApp ? 'Update' : 'Create'} Application</Button>
            </>
          }
        >
          <Form>
            <FormGroup>
              <Label for="name">Application Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My Application"
              />
            </FormGroup>
            <FormGroup>
              <Label for="description">Description (Optional)</Label>
              <Input
                type="textarea"
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of what this application does"
              />
            </FormGroup>
            <FormGroup>
              <Label>Allowed Audiences</Label>
              <div>
                {availableAudiences.map(audience => (
                  <FormGroup check inline key={audience}>
                    <Input
                      type="checkbox"
                      id={`audience-${audience}`}
                      checked={formData.allowed_audiences.includes(audience)}
                      onChange={() => toggleAudience(audience)}
                    />
                    <Label check for={`audience-${audience}`}>{audience}</Label>
                  </FormGroup>
                ))}
              </div>
            </FormGroup>
            <FormGroup>
              <Label>Scopes</Label>
              <div>
                {availableScopes.map(scope => (
                  <FormGroup check inline key={scope}>
                    <Input
                      type="checkbox"
                      id={`scope-${scope}`}
                      checked={formData.allowed_scopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                    />
                    <Label check for={`scope-${scope}`}>{scope}</Label>
                  </FormGroup>
                ))}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="redirect_uris">Redirect URIs</Label>
              <Input
                type="textarea"
                id="redirect_uris"
                value={formData.redirect_uris.join('\n')}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  redirect_uris: e.target.value.split('\n').filter(uri => uri.trim()) 
                })}
                placeholder={`${getDefaultRedirectUri()}\nhttps://myapp.com/oauth/callback`}
                rows="3"
              />
              <small className="text-muted">One URI per line</small>
            </FormGroup>
          </Form>
        </Modal>

        {/* Delete Modal */}
        <Modal
          open={deleteModal}
          onClose={() => setDeleteModal(false)}
          title="Delete OAuth Application"
          size="md"
          footer={
            <>
              <Button color="secondary" onClick={() => setDeleteModal(false)}>Cancel</Button>
              <Button color="danger" onClick={handleDeleteConfirm}>Delete Application</Button>
            </>
          }
        >
          Are you sure you want to delete <strong>{currentApp?.name}</strong>?
          This will immediately revoke access for all tokens issued to this application.
          This action cannot be undone.
        </Modal>

        {/* Tokens Modal */}
        <Modal
          open={tokensModal}
          onClose={() => setTokensModal(false)}
          title={`Active Tokens for ${currentApp?.name || ''}`}
          size="lg"
          footer={<Button color="secondary" onClick={() => setTokensModal(false)}>Close</Button>}
        >
          {appTokens.length === 0 ? (
            <div className="text-center p-4">
              <p>No active tokens found for this application.</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Audience</th>
                  <th>Scopes</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {appTokens.map((token) => (
                  <tr key={token.id}>
                    <td>
                      <div>
                        <strong>{token.user?.email || 'Unknown User'}</strong>
                      </div>
                    </td>
                    <td>
                      <Badge color="info">{token.audience}</Badge>
                    </td>
                    <td>
                      {token.scopes?.slice(0, 2).map(scope => (
                        <Badge key={scope} color="secondary" className="me-1">
                          {scope}
                        </Badge>
                      ))}
                      {token.scopes && token.scopes.length > 2 && (
                        <Badge color="light">+{token.scopes.length - 2} more</Badge>
                      )}
                    </td>
                    <td>
                      <div>
                        <div>{new Date(token.createdAt).toLocaleDateString()}</div>
                        <small className="text-muted">
                          {new Date(token.createdAt).toLocaleTimeString()}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div>{new Date(token.expiresAt).toLocaleDateString()}</div>
                        <small className="text-muted">
                          {new Date(token.expiresAt).toLocaleTimeString()}
                        </small>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${new Date(token.expiresAt) > new Date() ? 'bg-success' : 'bg-secondary'}`}>
                        {new Date(token.expiresAt) > new Date() ? 'Active' : 'Expired'}
                      </span>
                    </td>
                    <td>
                      {new Date(token.expiresAt) > new Date() && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleRevokeToken(token.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal>
      </div>
    </div>
  )
}

OAuthApplications.propTypes = {
  userStore: PropTypes.object.isRequired,
  className: PropTypes.string
}

export default withUserStore(OAuthApplications)